import { cn } from "@etudescarre/ui";

const dirtyCellClass = "bg-amber-100/60 dark:bg-amber-900/40";

/**
 * Flat in-cell text input, per the screen design rules
 * (.agents/screen-design.md): editable cells never show a framed input —
 * the cell itself is the editing surface. A hover/focus affordance and the
 * amber dirty highlight remain. Values wider than the cell end in an ellipsis
 * so a cut-off name reads as truncated, not as a different, shorter value;
 * callers pair it with a `title` so the full value shows on hover.
 */
export const cellInputClass = (dirty: boolean) =>
  cn(
    "w-full text-ellipsis rounded-md border border-transparent bg-transparent px-2 py-1 text-sm hover:border-zinc-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-zinc-100 dark:hover:border-zinc-700 aria-invalid:border-red-500 aria-invalid:placeholder:text-red-600 dark:aria-invalid:placeholder:text-red-400",
    dirty && dirtyCellClass,
  );

/** Amber dirty highlight for a `flat` `Select`, whose trigger is transparent. */
export const cellSelectClass = (dirty: boolean) =>
  cn("rounded-md", dirty && dirtyCellClass);

/**
 * Props of a required in-cell input. An empty field of a draft row says so in
 * the cell itself: a message under the input would break the fixed row height.
 */
export const requiredCellProps = (
  placeholder: string,
  value: string,
  dirty: boolean,
) => {
  const missing = dirty && !value.trim();
  return {
    required: true,
    "aria-invalid": missing,
    placeholder: missing ? "Requis" : placeholder,
  };
};

/**
 * Uppercases an input in place and returns its value. Writing it back through
 * React state alone would send the caret to the end of the field on every
 * letter typed mid-word.
 */
export const upperCaseInput = (input: HTMLInputElement): string => {
  const { selectionStart, selectionEnd } = input;
  input.value = input.value.toUpperCase();
  input.setSelectionRange(selectionStart, selectionEnd);
  return input.value;
};
